import { createContext, useContext, useState, type ReactNode } from "react";

export type Lang = "it" | "en";

const T: Record<string, Record<Lang, string>> = {
  // ── Navigazione sidebar ──
  "Dashboard":             { it: "Dashboard",              en: "Dashboard" },
  "Strutture":             { it: "Strutture",              en: "Properties" },
  "Lista strutture":       { it: "Lista strutture",        en: "Properties list" },
  "Camere":                { it: "Camere",                 en: "Rooms" },
  "Prenotazioni":          { it: "Prenotazioni",           en: "Bookings" },
  "Lista":                 { it: "Lista",                  en: "List" },
  "Calendario":            { it: "Calendario",             en: "Calendar" },
  "Sincronizzazione iCal": { it: "Sincronizzazione iCal",  en: "iCal Sync" },
  "Ospiti":                { it: "Ospiti",                 en: "Guests" },
  "Database ospiti":       { it: "Database ospiti",        en: "Guest database" },
  "Check-in online":       { it: "Check-in online",        en: "Online check-in" },
  "Contratti":             { it: "Contratti",              en: "Contracts" },
  "Guest App":             { it: "Guest App",              en: "Guest App" },
  "Configurazione":        { it: "Configurazione",         en: "Configuration" },
  "Anteprima":             { it: "Anteprima",              en: "Preview" },
  "Team":                  { it: "Team",                   en: "Team" },
  "Host":                  { it: "Host",                   en: "Host" },
  "Collaboratori":         { it: "Collaboratori",          en: "Staff" },
  "Pulizie":               { it: "Pulizie",                en: "Cleaning" },
  "Manutenzione":          { it: "Manutenzione",           en: "Maintenance" },
  "Finanze":               { it: "Finanze",                en: "Finances" },
  "Panoramica":            { it: "Panoramica",             en: "Overview" },
  "Entrate":               { it: "Entrate",                en: "Income" },
  "Uscite":                { it: "Uscite",                 en: "Expenses" },
  "Transazioni":           { it: "Transazioni",            en: "Transactions" },
  "Report":                { it: "Report",                 en: "Report" },
  "Operativo":             { it: "Operativo",              en: "Operations" },
  "Attività":              { it: "Attività",               en: "Tasks" },
  "Segnalazioni":          { it: "Segnalazioni",           en: "Issues" },
  "Recensioni":            { it: "Recensioni",             en: "Reviews" },
  "Notifiche":             { it: "Notifiche",              en: "Notifications" },
  "Avvisi":                { it: "Avvisi",                 en: "Notices" },
  "Messaggi":              { it: "Messaggi",               en: "Messages" },
  "Pricing":               { it: "Pricing",                en: "Pricing" },
  "Impostazioni":          { it: "Impostazioni",           en: "Settings" },
  "Super Admin":           { it: "Super Admin",            en: "Super Admin" },
  // ── Sidebar footer ──
  "Esci":                  { it: "Esci",                   en: "Log out" },
  "Simula vista Team":     { it: "Simula vista Team",      en: "Simulate Team view" },
  "Vista Team attiva":     { it: "Vista Team attiva",      en: "Team view active" },
  // ── Sezioni Impostazioni ──
  "Profilo":               { it: "Profilo",                en: "Profile" },
  "Sicurezza":             { it: "Sicurezza",              en: "Security" },
  "Lingua & Regione":      { it: "Lingua & Regione",       en: "Language & Region" },
  "Abbonamento":           { it: "Abbonamento",            en: "Subscription" },
  "Fatturazione":          { it: "Fatturazione",           en: "Billing" },
  "Aspetto":               { it: "Aspetto",                en: "Appearance" },
  "Account":               { it: "Account",                en: "Account" },
  // ── UI comuni ──
  "Salva":                 { it: "Salva",                  en: "Save" },
  "Salva modifiche":       { it: "Salva modifiche",        en: "Save changes" },
  "Annulla":               { it: "Annulla",                en: "Cancel" },
  "Elimina":               { it: "Elimina",                en: "Delete" },
  "Conferma":              { it: "Conferma",               en: "Confirm" },
  "Modifica":              { it: "Modifica",               en: "Edit" },
  "Aggiungi":              { it: "Aggiungi",               en: "Add" },
  "Caricamento...":        { it: "Caricamento...",         en: "Loading..." },
  "Attivo":                { it: "Attivo",                 en: "Active" },
  "Inattivo":              { it: "Inattivo",               en: "Inactive" },
  // ── Impostazioni Personale ──
  "Personale":             { it: "Personale",              en: "Personal" },
  "Gestione":              { it: "Gestione",               en: "Management" },
  "App":                   { it: "App",                    en: "App" },
  // ── Impostazioni Sicurezza ──
  "Password":              { it: "Password",               en: "Password" },
  "Cambia":                { it: "Cambia",                 en: "Change" },
  "Autenticazione 2FA":    { it: "Autenticazione 2FA",     en: "2FA Authentication" },
  "Sessioni attive":       { it: "Sessioni attive",        en: "Active sessions" },
  "Disconnetti":           { it: "Disconnetti",            en: "Sign out" },
  // ── Impostazioni Aspetto ──
  "Modalità tema":         { it: "Modalità tema",          en: "Theme mode" },
  "Tema colori":           { it: "Tema colori",            en: "Color theme" },
  "Light":                 { it: "Light",                  en: "Light" },
  "Dark":                  { it: "Dark",                   en: "Dark" },
  "Sistema":               { it: "Sistema",                en: "System" },
  "Chiaro classico":       { it: "Chiaro classico",        en: "Classic light" },
  "Scuro classico":        { it: "Scuro classico",         en: "Classic dark" },
  "Segue il dispositivo":  { it: "Segue il dispositivo",   en: "Follows device" },
};

interface LangCtx { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => string; }

const LanguageContext = createContext<LangCtx>({
  lang: "it",
  setLang: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(
    () => (localStorage.getItem("hedita_lang") as Lang) ?? "it"
  );
  const setLang = (l: Lang) => { setLangState(l); localStorage.setItem("hedita_lang", l); };
  const t = (key: string): string => T[key]?.[lang] ?? key;
  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);
